import React, { FC } from 'react'
import {
    ArticleAbstractProps,
    ArticleAuthorContentProps,
    ArticleSectionContentProps,
    ArticleSectionType,
} from '@sport1/types/web'
import NonFlexingContainer from '@sport1/news-styleguide/NonFlexingContainer'
import FlexingContainer from '@sport1/news-styleguide/FlexingContainer'
import dynamic from 'next/dynamic'
import colorPattern from '@sport1/news-styleguide/colorPattern'
import { useRouter } from 'next/router'
import ArticleAuthors from '../../Authors'
import ArticleAbstract from '../../Abstract'
import renderBaseArticleComponent from '@/helpers/renderBaseArticleComponent'
const SideColumn = dynamic(() => import('@/components/Article/Section/Standard/SideColumn'), {
    ssr: false,
})

type Props = {
    content: ArticleSectionContentProps[]
    isSponsored?: boolean
    isAdsActive?: boolean // to disable ads, need to pass 'false', otherwise ads enabled
    followLinks: boolean //if true - follows every external link
}

const TOP_AREA: string[] = [
    ArticleSectionType.HEADLINE,
    ArticleSectionType.ABSTRACT,
    ArticleSectionType.TOP_VIDEO,
    ArticleSectionType.TOP_IMAGE,
    ArticleSectionType.AUTHORS,
]

const IGNORED_ON_SPONSORED_PAGE: string[] = [
    ArticleSectionType.AD,
    ArticleSectionType.RELATED_CONTEXT_ITEMS,
]

const IGNORED_WHEN_ADS_DISABLED: string[] = [ArticleSectionType.AD]

const ArticleSection: FC<Props> = ({ content, isSponsored, isAdsActive = true, followLinks }) => {
    const topContent: ArticleSectionContentProps[] = []
    const mainAreaContent: ArticleSectionContentProps[] = []
    const { asPath } = useRouter()
    content.forEach(contentProps => {
        if (
            (isSponsored && IGNORED_ON_SPONSORED_PAGE.includes(contentProps.type)) ||
            (!isAdsActive && IGNORED_WHEN_ADS_DISABLED.includes(contentProps.type))
        ) {
            return
        }
        if (TOP_AREA.includes(contentProps.type)) {
            topContent.push(contentProps)
        } else {
            mainAreaContent.push(contentProps)
        }
    })

    const renderContent = (contentProps: ArticleSectionContentProps[]) => {
        return (
            <>
                {contentProps.map((component: ArticleSectionContentProps, index: number) => {
                    const pageComponent = renderBaseArticleComponent(component, index, followLinks)
                    if (pageComponent) {
                        return pageComponent
                    }
                })}
            </>
        )
    }

    const articleAuthorContent = topContent.find(
        (el): el is ArticleAuthorContentProps => el.type === ArticleSectionType.AUTHORS
    )

    const abstractContent = topContent.find(
        (el): el is ArticleAbstractProps => el.type === ArticleSectionType.ABSTRACT
    )

    return (
        <NonFlexingContainer className="article-section-container" marginBottom="spacing-11">
            <NonFlexingContainer>
                <NonFlexingContainer
                    backgroundColor={colorPattern.pearl}
                    paddingX={['spacing-6', 'spacing-9', 'spacing-11']}
                    paddingTop={['spacing-9', 'spacing-9', 'spacing-11']}
                    paddingBottom={['spacing-none', 'spacing-none', 'spacing-11']}
                >
                    {renderContent(topContent)}
                </NonFlexingContainer>
                <NonFlexingContainer
                    horizontal
                    testID="article-standard-section-inner-container-0"
                    marginTop={['spacing-none', 'spacing-none', 'spacing-none', 'spacing-11']}
                    className="items-start"
                >
                    <FlexingContainer
                        className={`article-section-main-area-container${
                            isSponsored ? '-sponsored' : ''
                        }`}
                        backgroundColor={colorPattern.pearl}
                        marginRight={[
                            'spacing-none',
                            'spacing-none',
                            'spacing-none',
                            isSponsored ? 'spacing-none' : 'spacing-11',
                        ]}
                        paddingTop={['spacing-9', 'spacing-9', 'spacing-11']}
                        {...((asPath.startsWith('/news/unternehmen') ||
                            asPath.startsWith('/unternehmen')) && { overflow: 'hidden' })}
                    >
                        <NonFlexingContainer
                            id="article-author-area"
                            paddingX={['spacing-6', 'spacing-9', 'spacing-11']}
                        >
                            {articleAuthorContent && (
                                <ArticleAuthors content={articleAuthorContent} />
                            )}
                            {abstractContent && (
                                <ArticleAbstract
                                    content={abstractContent}
                                    displayArray={['inline', 'none', 'none']}
                                />
                            )}
                        </NonFlexingContainer>
                        {renderContent(mainAreaContent)}
                    </FlexingContainer>
                    {!isSponsored ? (
                        <NonFlexingContainer
                            display={['none', 'none', 'none', 'flex']}
                            width={372} // width + paddings + ads need atleast 300px
                            backgroundColor={colorPattern.pearl}
                            padding="spacing-11"
                            testID="article-standard-section-inner-container-1"
                        >
                            <SideColumn isAdsActive={isAdsActive} />
                        </NonFlexingContainer>
                    ) : null}
                </NonFlexingContainer>
            </NonFlexingContainer>
        </NonFlexingContainer>
    )
}

export default ArticleSection
